import React, { ReactElement } from 'react'

import Zoom from 'react-medium-image-zoom'

import Image from 'next/image'

import { Link } from '@/i18n/navigation'

import { OutputBlockData } from '@editorjs/editorjs'
import clsx from 'clsx'

import {
    addTargetToLinks,
    generateImagePlaceHolder,
    sanitizeHtml,
} from '@/lib/utils'

import styles from './styles.module.scss'

interface Props {
    item: OutputBlockData
    index: number
    data: OutputBlockData[]
}

export default function ArticleBlockItem({
    item,
    index,
    data,
}: Props): ReactElement {
    switch (item.type) {
        case 'header':
            return React.createElement(`h${item.data.level}`, {
                style: item.tunes?.align?.alignment
                    ? {
                          textAlign: item.tunes.align.alignment,
                      }
                    : undefined,
                className: clsx(
                    styles.block__title,
                    styles[`block__title-${item.data.level}`]
                ),
                dangerouslySetInnerHTML: {
                    __html: sanitizeHtml(item.data.text),
                },
            })
        case 'paragraph':
            if (index > 0 && data[index - 1].type === 'toggle') {
                return <></>
            }
            return (
                <div
                    style={
                        item.tunes?.align?.alignment
                            ? { textAlign: item.tunes.align.alignment }
                            : undefined
                    }
                    className={styles.block__paragraph}
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(addTargetToLinks(item.data.text)),
                    }}
                />
            )
        case 'warning':
            return (
                <div className={styles.block__alert}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                                addTargetToLinks(item.data.title)
                            ),
                        }}
                    />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                                addTargetToLinks(item.data.message)
                            ),
                        }}
                    />
                </div>
            )
        case 'alert':
            return (
                <div
                    className={clsx(styles.block__colored, {
                        [styles['block__colored-primary']]:
                            item.data.type === 'primary',
                        [styles['block__colored-secondary']]:
                            item.data.type === 'secondary',
                        [styles['block__colored-info']]:
                            item.data.type === 'info',
                        [styles['block__colored-success']]:
                            item.data.type === 'success',
                    })}
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(item.data.message),
                    }}
                    style={{
                        textAlign:
                            (item.data.align as 'left' | 'center' | 'right') ??
                            'left',
                    }}
                />
            )
        case 'list':
            if (index > 0 && data[index - 1].type === 'toggle') {
                return <></>
            }
            if (item.data.style === 'checklist') {
                return (
                    <div className={styles.block__checkbox}>
                        {item.data.items.map(
                            (
                                checkbox: {
                                    content: string
                                    meta: { checked: boolean }
                                },
                                index: number
                            ) => (
                                <div key={`checkbox-${index}`}>
                                    <div
                                        className={
                                            styles[
                                                `block__checkbox-${checkbox.meta.checked ? 'checked' : 'unchecked'}`
                                            ]
                                        }
                                    />
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(
                                                addTargetToLinks(
                                                    checkbox.content
                                                )
                                            ),
                                        }}
                                    />
                                </div>
                            )
                        )}
                    </div>
                )
            }
            if (item.data.style === 'ordered') {
                return (
                    <div className={styles.block__ol}>
                        {item.data.items.map(
                            (item: { content: string }, index: number) => (
                                <div key={`ol-${index}`}>
                                    <span>{++index}</span>
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(item.content),
                                        }}
                                    />
                                </div>
                            )
                        )}
                    </div>
                )
            }
            return (
                <div className={styles.block__ul}>
                    {item.data.items.map(
                        (item: { content: string }, index: number) => (
                            <div key={`ol-${index}`}>
                                <span />
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(item.content),
                                    }}
                                />
                            </div>
                        )
                    )}
                </div>
            )
        case 'linkTool':
            return (
                <div className={styles.block__link}>
                    <Link
                        href={item.data.link ?? ''}
                        target={'_blank'}
                        rel={'noopener'}
                    >
                        <div>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(item.data.meta?.title),
                                }}
                            />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(
                                        item.data.meta?.description ?? ''
                                    ),
                                }}
                            />
                        </div>
                    </Link>
                </div>
            )
        case 'delimiter':
            return (
                <div className={styles.block__divider}>
                    <div />
                </div>
            )
        case 'image':
            if (item.data.link) {
                return (
                    <Link
                        href={item.data.link}
                        target={'_blank'}
                        rel={'noopener'}
                        className={styles['block__image-link']}
                    >
                        <div className={styles.block__image}>
                            <Image
                                width={957}
                                height={538}
                                draggable={false}
                                unoptimized={true}
                                src={item.data.file?.url ?? ''}
                                alt={item.data.caption ?? ''}
                            />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(item.data.caption),
                                }}
                            />
                        </div>
                    </Link>
                )
            }
            return (
                <div className={styles.block__image}>
                    <Zoom>
                        <Image
                            width={957}
                            height={538}
                            draggable={false}
                            unoptimized={true}
                            src={item.data.file?.url ?? ''}
                            alt={item.data.caption ?? ''}
                            placeholder={generateImagePlaceHolder(957, 538)}
                        />
                    </Zoom>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(item.data.caption),
                        }}
                    />
                </div>
            )
        case 'attaches':
            const title = item.data.title?.split('.')
            return (
                <div
                    className={styles.block__files}
                    style={
                        index > 0 && data[index - 1].type !== 'attaches'
                            ? {
                                  borderTopLeftRadius: 16,
                                  borderTopRightRadius: 16,
                              }
                            : undefined
                    }
                >
                    <Link
                        href={item.data.file?.url ?? ''}
                        target={'_blank'}
                        rel={'noopener'}
                    >
                        {Array.isArray(title) ? (
                            <p>
                                {sanitizeHtml(title[0])}
                                <span>
                                    {'.'}
                                    {sanitizeHtml(title[1])}
                                </span>
                            </p>
                        ) : null}
                        <p>
                            {item.data.file?.customSize
                                ? `(${item.data.file.customSize})`
                                : ''}
                        </p>
                    </Link>
                </div>
            )
        case 'embed':
            if (item.data.service === 'youtube' || item.data.embed) {
                return (
                    <div
                        className={styles.block__embed}
                        dangerouslySetInnerHTML={{
                            __html: `<iframe width="100%" height="auto" src="${item.data.embed}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`,
                        }}
                    />
                )
            }
            return <></>
        case 'table':
            if (!Array.isArray(item.data.content)) {
                return <></>
            }
            return (
                <div
                    className={clsx(styles.block__table, {
                        [styles['block__table-headings']]:
                            !!item.data.withHeadings,
                    })}
                >
                    <table>
                        <tbody>
                            {item.data.content.map(
                                (trItem: string[], trIndex: number) => (
                                    <tr key={`tr-${trIndex}`}>
                                        {trItem.map((tdItem, tdIndex) => (
                                            <td
                                                key={`td-${tdIndex}`}
                                                dangerouslySetInnerHTML={{
                                                    __html: sanitizeHtml(
                                                        addTargetToLinks(tdItem)
                                                    ),
                                                }}
                                            />
                                        ))}
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                </div>
            )
        default:
            return <></>
    }
}
